import useDisablePropagationInMap from "@/hooks/useDisablePropagationInMap";
import useTraces from "@/hooks/useTraces";
import useGlobalStore from "@/services/store/useGlobalStore";
import usePlayer from "@/services/store/usePlayer";
import { getMinMaxDateFromTraces } from "@/utils/functions/trace";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import { Box, Grid, IconButton, Slider, Stack } from "@mui/material";
import dayjs from "dayjs";
import { useEffect, useRef } from "react";
import SpeedControl from "./components/SpeedControl";

const Player = () => {
  const playerRef = useRef(null);

  // get traces data in global store
  const selectedTraceIds = useGlobalStore((state) => state.selectedTraceIds);
  const { tracesById } = useTraces();
  // get infos of selected trace ids from tracesById
  const selectedTraces = selectedTraceIds.map((id: number) => tracesById?.[id]).filter((trace) => !!trace);

  useDisablePropagationInMap({ ref: playerRef, deps: [selectedTraces] });

  // get player info from player store
  const isPlaying = usePlayer((state) => state.isPlaying);
  const timestamp = usePlayer((state) => state.timestamp);
  const play = usePlayer((state) => state.play);
  const pause = usePlayer((state) => state.pause);
  const setTimestamp = usePlayer((state) => state.setTimestamp);
  const initialiseTimer = usePlayer((state) => state.initialiseTimer);
  const replay = usePlayer((state) => state.replay);

  const minDate = usePlayer((state) => state.minDate);
  const maxDate = usePlayer((state) => state.maxDate);

  const speed = usePlayer((state) => state.speed);

  const handlePause = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    pause();
  };

  const handlePlay = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (timestamp >= maxDate) replay();
    else play();
  };

  const handleSliderChange = (e: Event, value: number) => {
    e.stopPropagation();
    setTimestamp(value);
    pause();
  };

  // When timestamp, dates or isPlaying change, start a timer that increase the current timestamp and clear the previous timers.
  useEffect(() => {
    if (isPlaying) {
      // 100ms timer to see smooth slider
      const timeout = setTimeout(() => {
        if (timestamp < maxDate) {
          setTimestamp(timestamp + 100 * speed);
        } else {
          pause();
        }
      }, 100);

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [isPlaying, timestamp, minDate, maxDate, speed]);

  // when traces selection change, reset everything and restart the player after calculating min/max dates
  useEffect(() => {
    if (selectedTraces.length) {
      const minMax = getMinMaxDateFromTraces(selectedTraces);
      initialiseTimer(minMax.minDate, minMax.maxDate);
    }
  }, [JSON.stringify(selectedTraces)]);

  if (!selectedTraceIds.length) return null;

  return (
    <Box
      ref={playerRef}
      sx={{
        position: "absolute",
        zIndex: 9999,
        bottom: 10,
        left: "50%",
        transform: "translate(-50%, 0)",
        background: (theme: ThemeType) => theme.palette.secondary.main,
        borderRadius: 3,
      }}
      onDrag={(e) => e.stopPropagation()}>
      <Stack sx={{ p: 2 }} spacing={2}>
        <Box>
          <Grid container spacing={2}>
            <Grid item md={9}>
              <Stack height="100%" alignItems="center" justifyContent="center">
                <Slider
                  aria-label="Timer slider"
                  valueLabelDisplay="auto"
                  valueLabelFormat={(value: number) => dayjs(value).format("DD/MM/YYYY HH:mm:ss")}
                  step={1000}
                  onChange={(e, value) => handleSliderChange(e, value as number)}
                  value={timestamp}
                  max={maxDate}
                  min={minDate}
                />
              </Stack>
            </Grid>
            <Grid item md={3}>
              {isPlaying ? (
                <IconButton onClick={handlePause}>
                  <PauseCircleIcon sx={{ color: (theme) => theme.palette.primary.main }} />
                </IconButton>
              ) : (
                <IconButton onClick={handlePlay} sx={{ color: (theme) => theme.palette.primary.main }}>
                  <PlayCircleIcon />
                </IconButton>
              )}
            </Grid>
          </Grid>
        </Box>

        <SpeedControl />
      </Stack>
    </Box>
  );
};

export default Player;
